// src/components/AdminDashboard/AdminDashboard.js
import React, { useState, useEffect } from 'react';
// Ensure correct import path for Firestore (textdb)
import { textdb } from '../../firebaseConfig'; // Keep Firestore import
// REMOVED Firebase Auth imports: getAuth, signInWithEmailAndPassword, onAuthStateChanged, signOut
import { collection, getDocs, query, orderBy, doc, deleteDoc, writeBatch } from "firebase/firestore";
import { CSVLink } from "react-csv";
import * as XLSX from 'xlsx';
import './AdminDashboard.css'; // Make sure this CSS file exists

// --- Hardcoded Credentials (INSECURE - FOR DEMO/LOW-RISK ONLY) ---
const ADMIN_EMAIL = "coachnachiketh@gmail.com";
const ADMIN_PASSWORD = "445566"; // Anyone viewing source can see this!

const AdminDashboard = () => {
    // --- Authentication State (Simple Boolean) ---
    const [isLoggedIn, setIsLoggedIn] = useState(false); // Track login status
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loginError, setLoginError] = useState('');

    // --- Dashboard State ---
    const [bookings, setBookings] = useState([]);
    const [loading, setLoading] = useState(false); // Data loading state (only relevant when logged in)
    const [error, setError] = useState(''); // Data fetching/deleting error state
    const [isDeletingAll, setIsDeletingAll] = useState(false);

    // --- Effect to Fetch Bookings ONLY when Logged In ---
    useEffect(() => {
        if (isLoggedIn) { // Only fetch if manually logged in
            fetchBookings();
        } else {
            // Clear data if user logs out or is not logged in
            setBookings([]);
            setError('');
            setLoading(false); // Ensure loading is off if logged out
            setIsDeletingAll(false); // Reset delete all state on logout
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isLoggedIn]); // Re-run this effect when the isLoggedIn state changes

    // --- Login Handler (Simple Check) ---
    const handleLogin = (e) => {
        e.preventDefault(); // Prevent form submission reload
        setLoginError(''); // Clear previous errors

        // --- The core check against hardcoded values ---
        if (email === ADMIN_EMAIL && password === ADMIN_PASSWORD) {
            setIsLoggedIn(true);
            // Optionally clear fields after successful login
            // setEmail('');
            // setPassword('');
        } else {
            setLoginError('Invalid email or password.');
            setIsLoggedIn(false); // Ensure logged out state
        }
    };

    // --- Logout Handler ---
    const handleLogout = () => {
        setIsLoggedIn(false);
        // Clear form fields for next login attempt
        setEmail('');
        setPassword('');
        setLoginError('');
        // The useEffect depending on isLoggedIn will handle clearing booking data
    };


    // --- Fetch Bookings Function (No change needed here) ---
    const fetchBookings = async () => {
        setLoading(true); // Data loading starts
        setError('');
        try {
            const bookingsCollectionRef = collection(textdb, "bookings");
            const q = query(bookingsCollectionRef, orderBy("submittedAt", "desc"));
            const querySnapshot = await getDocs(q);
            const bookingsData = querySnapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                submittedAt: doc.data().submittedAt?.toDate
                             ? doc.data().submittedAt.toDate().toLocaleString('en-US', {
                                 year: 'numeric', month: 'short', day: 'numeric',
                                 hour: '2-digit', minute: '2-digit', hour12: true
                             })
                             : 'N/A'
            }));
            setBookings(bookingsData);
        } catch (err) {
            console.error("Error fetching bookings: ", err);
            setError('Failed to load booking data. Please refresh the page.');
        } finally {
            setLoading(false); // Data loading finished
        }
    };

    // --- Single Delete Handler (No change needed here) ---
    const handleDelete = async (bookingId) => {
        if (!window.confirm(`Are you sure you want to delete this booking?`)) return;
        setError(''); // Clear previous errors
        try {
            const bookingDocRef = doc(textdb, "bookings", bookingId);
            await deleteDoc(bookingDocRef);
            setBookings(prevBookings => prevBookings.filter(booking => booking.id !== bookingId));
            console.log("Booking deleted successfully:", bookingId);
        } catch (err) {
            console.error("Error deleting booking: ", err);
            setError(`Failed to delete booking ${bookingId}. Please try again.`);
        }
    };

    // --- Delete All Handler (No change needed here) ---
    const handleDeleteAll = async () => {
        if (!window.confirm("DELETE ALL BOOKINGS?\n\nAre you absolutely sure you want to delete ALL booking submissions? This action cannot be undone.")) return;
        if (!window.confirm("FINAL CONFIRMATION:\n\nDelete ALL bookings permanently?")) return;

        setIsDeletingAll(true);
        setError('');
        try {
            const bookingsCollectionRef = collection(textdb, "bookings");
            const querySnapshot = await getDocs(bookingsCollectionRef);
            if (querySnapshot.empty) {
                 setError("No bookings found to delete.");
                 setIsDeletingAll(false);
                 return;
            }
            const batch = writeBatch(textdb);
            querySnapshot.docs.forEach((bookingDoc) => {
                batch.delete(bookingDoc.ref);
            });
            await batch.commit();
            setBookings([]);
            console.log("All bookings deleted successfully.");
        } catch (err) {
            console.error("Error deleting all bookings: ", err);
            setError('Failed to delete all bookings. Some items might remain. Please refresh and try again.');
            // Refetch needed in case of partial failure, but only if still logged in
            if (isLoggedIn) {
                 fetchBookings();
            }
        } finally {
            setIsDeletingAll(false);
        }
    };

    // --- Export Logic (No change needed here) ---
     const csvHeaders = [
         { label: "Full Name", key: "fullName" },
         { label: "WhatsApp Number", key: "whatsappNumber" },
         { label: "Weight (kg)", key: "weight" },
         { label: "Age", key: "age" },
         { label: "Investment Comfort", key: "investmentComfort" },
         { label: "Submitted At", key: "submittedAt" },
         { label: "Firestore ID", key: "id"}
     ];

     const exportToExcel = () => {
         const wb = XLSX.utils.book_new();
         const wsData = bookings.map(booking => ({
             "Full Name": booking.fullName,
             "WhatsApp Number": booking.whatsappNumber,
             "Weight (kg)": booking.weight,
             "Age": booking.age,
             "Investment Comfort": booking.investmentComfort,
             "Submitted At": booking.submittedAt
         }));
         const ws = XLSX.utils.json_to_sheet(wsData);
          ws['!cols'] = [
              { wch: 20 }, { wch: 18 }, { wch: 12 }, { wch: 8 }, { wch: 35 }, { wch: 25 }
          ];
         XLSX.utils.book_append_sheet(wb, ws, "Bookings");
         XLSX.writeFile(wb, `BookingsData_${new Date().toISOString().slice(0,10)}.xlsx`);
     };


    // --- Render Logic ---

    // 1. If not logged in, show Login Form
    if (!isLoggedIn) {
        return (
            <div className="admin-container login-container">
                <h2>Admin Login</h2>
                 {/* Add a warning about the insecurity */}
               
                <form onSubmit={handleLogin}>
                    <div className="form-group">
                        <label htmlFor="email">Email:</label>
                        <input
                            type="email"
                            id="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            placeholder="Enter admin email" // More generic placeholder
                        />
                    </div>
                    <div className="form-group">
                        <label htmlFor="password">Password:</label>
                        <input
                            type="password"
                            id="password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                            placeholder="Enter admin password"
                        />
                    </div>
                    {loginError && <p className="error-message login-error">{loginError}</p>}
                    <button type="submit" className="login-button">
                        Login
                    </button>
                </form>
            </div>
        );
    }

    // 2. If logged in, show the Dashboard
    return (
        <div className="admin-container">
            <div className="admin-header">
                 <div>
                    <h2>Booking Submissions ({bookings.length})</h2>
                    {/* You could display the hardcoded email, but it's less meaningful than with real auth */}
                    {/* <p className="user-email">Access Granted</p> */}
                 </div>
                 <button onClick={handleLogout} className="logout-button">
                     Logout
                 </button>
            </div>
            {/* Display data loading indicator or data errors prominently */}
            {loading && <div className="loading-state"><p>Loading booking data...</p></div>}
            {error && <p className="error-message data-error">{error}</p>}

            {!loading && bookings.length > 0 ? (
                <>
                    <div className="admin-actions">
                         <div className="export-buttons">
                            <CSVLink
                                data={bookings}
                                headers={csvHeaders}
                                filename={`bookings-data_${new Date().toISOString().slice(0,10)}.csv`}
                                className="export-button csv-button"
                                target="_blank"
                            >
                                Export CSV
                            </CSVLink>
                            <button onClick={exportToExcel} className="export-button excel-button" disabled={isDeletingAll}>
                                Export Excel
                            </button>
                        </div>
                        <div className="delete-all-section">
                             <button
                                onClick={handleDeleteAll}
                                className="delete-button delete-all-button"
                                disabled={isDeletingAll || loading} // Also disable if data is loading
                             >
                                {isDeletingAll ? 'Deleting All...' : 'Delete All Bookings'}
                             </button>
                        </div>
                    </div>

                    <div className="table-container">
                        <table>
                            <thead>
                                <tr>
                                    <th>Full Name</th>
                                    <th>WhatsApp</th>
                                    <th>Weight</th>
                                    <th>Age</th>
                                    <th>Investment Comfort</th>
                                    <th>Submitted At</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bookings.map((booking) => (
                                    <tr key={booking.id}>
                                        <td data-label="Full Name">{booking.fullName}</td>
                                        <td data-label="WhatsApp">{booking.whatsappNumber}</td>
                                        <td data-label="Weight">{booking.weight} kg</td>
                                        <td data-label="Age">{booking.age}</td>
                                        <td data-label="Investment Comfort">{booking.investmentComfort}</td>
                                        <td data-label="Submitted At">{booking.submittedAt}</td>
                                        <td data-label="Actions" className="actions-cell">
                                            <button
                                                onClick={() => handleDelete(booking.id)}
                                                className="delete-button single-delete-button"
                                                disabled={isDeletingAll || loading} // Also disable if data is loading
                                                title={`Delete booking for ${booking.fullName}`}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : (
                 // Display message when no bookings exist (and not loading/no error)
                 !loading && !error && (
                     <div className="no-bookings-message">
                        <p>No booking submissions found.</p>
                     </div>
                 )
            )}
        </div>
    );
};

export default AdminDashboard;